import { BaseException, ListOutput, StandardResponse } from '@allocab/allocab-commons-shared-types';
import { captureException } from '@sentry/react';
import axios from 'axios';
import type { AxiosError, AxiosHeaderValue, AxiosInstance } from 'axios';

import type { ApiClient, APICodeError, APINetworkError, APIResponseDataType, APIServerError, Options } from './types';
import { getLocale, t } from '../translations/translation';

export class Api implements ApiClient {
  axiosInstance: AxiosInstance;
  locale: string = getLocale();
  freshTokensCallback: () => Promise<string | null>;
  onStandardError: (_error: StandardResponse<BaseException>) => void;
  getCustomHeaders: () => Record<string, AxiosHeaderValue>;

  constructor(
    baseURL: string,
    freshTokensCallback: () => Promise<string | null>,
    onInvalidToken: (_reason?: 'INVALID_TOKEN' | 'INVALID_USER_TYPE') => void,
  ) {
    this.freshTokensCallback = freshTokensCallback;
    this.onStandardError = () => {};
    this.getCustomHeaders = () => ({});
    this.axiosInstance = axios.create({ baseURL, timeout: 60 * 1000 });
    this.axiosInstance.interceptors.response.use(
      response => {
        if (response?.headers?.['content-type'] === 'application/pdf') {
          return response?.data;
        }

        return response?.data?.data;
      },
      (error: AxiosError) => {
        if (error.response) {
          // The request was made and the server responded with an expected error (functional or auth for example)
          let message = t('pwa.network.server-error');
          const config = {
            baseURL: error.response.config.baseURL,
            url: error.response.config.url,
            params: error.response.config.params,
          };

          const contentType = error.response.headers['content-type'];
          if (typeof contentType === 'string' && contentType.includes('application/json')) {
            const result = error.response.data as StandardResponse<BaseException>;

            this.onStandardError(result);

            const errorMessage = result.data.errorMessage;
            if (result.message) message = result.message;

            if (errorMessage === 'INVALID_TOKEN' || errorMessage === 'INVALID_USER_TYPE') {
              onInvalidToken(errorMessage);
            }

            if (error.response.status === 400) {
              captureException(errorMessage, {
                tags: { API_ERROR_TYPE: 'server' },
                extra: { config, userMessage: message },
              });
            }
          } else {
            captureException(`Expected JSON response from the API, got: ${error.response.headers['content-type']}`, {
              tags: { API_ERROR_TYPE: 'server' },
              extra: { config },
            });
          }

          throw {
            type: 'server',
            nativeError: error,
            statusCode: error.response.status,
            config: {
              baseURL: error.response.config.baseURL,
              url: error.response.config.url,
              params: error.response.config.params,
            },
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            ...error.response.data,
          } as APIServerError;
        }

        if (error.request) {
          // The request was made but no response was received (no network for example)
          throw {
            type: 'network',
            nativeError: error,
            message: t('pwa.network.network-error'),
          } satisfies APINetworkError;
        }

        // Something happened in setting up the request that triggered an Error
        captureException(error, { tags: { API_ERROR_TYPE: 'code' } });

        throw {
          type: 'code',
          nativeError: error,
          message: t('pwa.network.code-error'),
        } satisfies APICodeError;
      },
    );
  }

  getDefaultHeaders = () => {
    const customHeaders = this.getCustomHeaders();

    return {
      'Accept-Language': this.locale,
      ...customHeaders,
    };
  };

  setGetCustomHeaders = (getCustomHeaders: () => Record<string, AxiosHeaderValue>) => {
    this.getCustomHeaders = getCustomHeaders;
  };

  setLocale = (locale: string) => {
    this.locale = locale;
  };

  setOnStandardError = (onStandardError: (_error: StandardResponse<BaseException>) => void) => {
    this.onStandardError = onStandardError;
  };

  get = async <Input, Output, Format extends APIResponseDataType = 'item'>(
    path: string,
    params?: Input,
    { skipAuth, ...axiosConfig }: Options = {},
  ) => {
    const bearerToken = skipAuth ? null : await this.freshTokensCallback();

    if (!skipAuth && !bearerToken) {
      console.error('Expected bearer but found none', JSON.stringify({ path, params, bearerToken }));
    }

    return this.axiosInstance.get<undefined, Format extends 'list' ? ListOutput<Output> : Output>(path, {
      ...axiosConfig,
      params,
      headers: {
        ...this.getDefaultHeaders(),
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        ...axiosConfig.headers,
      },
    });
  };

  post = async <Input, Output, Format extends APIResponseDataType = 'item'>(
    path: string,
    data?: Input,
    { skipAuth, ...axiosConfig }: Options = {},
  ) => {
    const bearerToken = skipAuth ? null : await this.freshTokensCallback();

    return this.axiosInstance.post<undefined, Format extends 'list' ? ListOutput<Output> : Output>(path, data, {
      ...axiosConfig,
      headers: {
        ...this.getDefaultHeaders(),
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        ...axiosConfig.headers,
      },
    });
  };

  postFormData = async <Output, Format extends APIResponseDataType = 'item'>(
    path: string,
    data: FormData,
    { skipAuth, ...axiosConfig }: Options = {},
  ) => {
    const bearerToken = skipAuth ? null : await this.freshTokensCallback();

    return this.axiosInstance.post<undefined, Format extends 'list' ? ListOutput<Output> : Output>(path, data, {
      ...axiosConfig,
      headers: {
        ...this.getDefaultHeaders(),
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        'Content-Type': 'multipart/form-data',
        ...axiosConfig.headers,
      },
    });
  };

  put = async <Input, Output, Format extends APIResponseDataType = 'item'>(
    path: string,
    data: Input,
    { skipAuth, ...axiosConfig }: Options = {},
  ) => {
    const bearerToken = skipAuth ? null : await this.freshTokensCallback();

    return this.axiosInstance.put<undefined, Format extends 'list' ? ListOutput<Output> : Output>(path, data, {
      ...axiosConfig,
      headers: {
        ...this.getDefaultHeaders(),
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        ...axiosConfig.headers,
      },
    });
  };

  patch = async <Input, Output, Format extends APIResponseDataType = 'item'>(
    path: string,
    data?: Input,
    { skipAuth, ...axiosConfig }: Options = {},
  ) => {
    const bearerToken = skipAuth ? null : await this.freshTokensCallback();

    return this.axiosInstance.patch<undefined, Format extends 'list' ? ListOutput<Output> : Output>(path, data, {
      ...axiosConfig,
      headers: {
        ...this.getDefaultHeaders(),
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        ...axiosConfig.headers,
      },
    });
  };

  delete = async <Input, Output, Format extends APIResponseDataType = 'item'>(
    path: string,
    data?: Input,
    { skipAuth, ...axiosConfig }: Options = {},
  ) => {
    const bearerToken = skipAuth ? null : await this.freshTokensCallback();

    return this.axiosInstance.delete<undefined, Format extends 'list' ? ListOutput<Output> : Output>(path, {
      ...axiosConfig,
      data,
      headers: {
        ...this.getDefaultHeaders(),
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        ...axiosConfig.headers,
      },
    });
  };
}
